/**
 * AnalysisResult Management Page
 * Task 17.7: AnalysisResult管理UIの構築
 * Requirements: 23.1, 23.2, 23.3, 23.4, 23.5, 23.6, 23.7, 23.8, 23.9, 23.10, 23.11
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  DeleteSweep as BulkDeleteIcon,
} from '@mui/icons-material';
import {
  getAnalysisResults,
  getAnalysisResultById,
  updateAnalysisResult,
  deleteAnalysisResult,
  bulkDeleteAnalysisResults,
  exportAnalysisResultsJSON,
  exportAnalysisResultsCSV,
  AnalysisResult,
  GetAnalysisResultsParams,
  UpdateAnalysisResultInput,
} from '../api/analysisResultApi';

export default function AnalysisResultManagementPage() {
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [userIdFilter, setUserIdFilter] = useState('');
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState('');
  const [createdAtToFilter, setCreatedAtToFilter] = useState('');

  // Dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<AnalysisResult | null>(null);

  // Form state
  const [formData, setFormData] = useState<UpdateAnalysisResultInput>({});

  // Load results
  const loadResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetAnalysisResultsParams = {
        userId: userIdFilter || undefined,
        createdAtFrom: createdAtFromFilter || undefined,
        createdAtTo: createdAtToFilter || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      const response = await getAnalysisResults(params);
      setResults(response.results);
      setTotalCount(response.pagination.total);
    } catch (err) {
      setError('Failed to load analysis results');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults();
  }, [page, rowsPerPage]);

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPage(0);
    loadResults();
  };

  // View result
  const handleView = async (id: string) => {
    try {
      const result = await getAnalysisResultById(id);
      setSelectedResult(result);
      setViewDialogOpen(true);
    } catch (err) {
      setError('Failed to load result details');
      console.error(err);
    }
  };

  // Edit result
  const handleEdit = async (id: string) => {
    try {
      const result = await getAnalysisResultById(id);
      setSelectedResult(result);
      setFormData({
        scores: result.scores,
        patternId: result.pattern?.id,
        patternDescription: result.patternDescription || undefined,
      });
      setEditDialogOpen(true);
    } catch (err) {
      setError('Failed to load result details');
      console.error(err);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedResult) return;

    try {
      await updateAnalysisResult(selectedResult.id, formData);
      setEditDialogOpen(false);
      setSelectedResult(null);
      setFormData({});
      loadResults();
    } catch (err) {
      setError('Failed to update result');
      console.error(err);
    }
  };

  // Delete result
  const handleDeleteClick = (result: AnalysisResult) => {
    setSelectedResult(result);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedResult) return;

    try {
      await deleteAnalysisResult(selectedResult.id);
      setDeleteDialogOpen(false);
      setSelectedResult(null);
      loadResults();
    } catch (err) {
      setError('Failed to delete result');
      console.error(err);
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const count = await bulkDeleteAnalysisResults({
        createdAtFrom: createdAtFromFilter || undefined,
        createdAtTo: createdAtToFilter || undefined,
      });
      setBulkDeleteDialogOpen(false);
      setError(null);
      alert(`Deleted ${count} results`);
      loadResults();
    } catch (err) {
      setError('Failed to bulk delete results');
      console.error(err);
    }
  };

  // Export
  const handleExportJSON = async () => {
    try {
      const data = await exportAnalysisResultsJSON({
        userId: userIdFilter || undefined,
        createdAtFrom: createdAtFromFilter || undefined,
        createdAtTo: createdAtToFilter || undefined,
      });
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis-results-${new Date().toISOString()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export as JSON');
      console.error(err);
    }
  };

  const handleExportCSV = async () => {
    try {
      const data = await exportAnalysisResultsCSV({
        userId: userIdFilter || undefined,
        createdAtFrom: createdAtFromFilter || undefined,
        createdAtTo: createdAtToFilter || undefined,
      });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis-results-${new Date().toISOString()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export as CSV');
      console.error(err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">AnalysisResult Management</Typography>
        <Box>
          <Button startIcon={<DownloadIcon />} onClick={handleExportJSON} sx={{ mr: 1 }}>
            Export JSON
          </Button>
          <Button startIcon={<DownloadIcon />} onClick={handleExportCSV} sx={{ mr: 1 }}>
            Export CSV
          </Button>
          <Button startIcon={<BulkDeleteIcon />} color="error" onClick={handleBulkDelete}>
            Bulk Delete
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="User ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Created From"
            type="date"
            value={createdAtFromFilter}
            onChange={(e) => setCreatedAtFromFilter(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Created To"
            type="date"
            value={createdAtToFilter}
            onChange={(e) => setCreatedAtToFilter(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
          <Button variant="contained" onClick={handleApplyFilters}>
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setUserIdFilter('');
              setCreatedAtFromFilter('');
              setCreatedAtToFilter('');
            }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Pattern</TableCell>
              <TableCell>Scores</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No results found
                </TableCell>
              </TableRow>
            ) : (
              results.map((result) => (
                <TableRow key={result.id}>
                  <TableCell>{result.user?.name || '-'}</TableCell>
                  <TableCell>{result.pattern?.name || '-'}</TableCell>
                  <TableCell>{JSON.stringify(result.scores).substring(0, 30)}...</TableCell>
                  <TableCell>{new Date(result.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(result.id)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(result.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(result)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Result Details</DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="ID" value={selectedResult.id} InputProps={{ readOnly: true }} />
              <TextField
                label="User"
                value={`${selectedResult.user?.name} (${selectedResult.user?.email})`}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Pattern"
                value={selectedResult.pattern?.name || 'None'}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Scores (JSON)"
                value={JSON.stringify(selectedResult.scores, null, 2)}
                multiline
                rows={5}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Pattern Description"
                value={selectedResult.patternDescription || '-'}
                multiline
                rows={3}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Created At"
                value={new Date(selectedResult.createdAt).toLocaleString()}
                InputProps={{ readOnly: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Result</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Scores (JSON)"
              value={JSON.stringify(formData.scores || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setFormData({ ...formData, scores: parsed });
                } catch (err) {
                  // Invalid JSON, ignore
                }
              }}
              multiline
              rows={5}
            />
            <TextField
              label="Pattern ID"
              value={formData.patternId || ''}
              onChange={(e) => setFormData({ ...formData, patternId: e.target.value })}
            />
            <TextField
              label="Pattern Description"
              value={formData.patternDescription || ''}
              onChange={(e) => setFormData({ ...formData, patternDescription: e.target.value })}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this analysis result?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all results matching the current filters?
          </Typography>
          {createdAtFromFilter && (
            <Typography variant="body2">From: {createdAtFromFilter}</Typography>
          )}
          {createdAtToFilter && <Typography variant="body2">To: {createdAtToFilter}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
